import { inject, Injectable } from "@angular/core";
import { GasService } from "@domain/gas/gas.service";
import { FGDS_REGARD_3000_PLUS_MATERIAL_TYPE_MAP, resolveFgdsProductType } from "@domain/product/fgds-product-type-resolver";
import { FgdsProductType, Product } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { Regard3000Configuration } from "@domain/project/configurations/regard-3000-configuration";
import { FgdsProject as Project } from "@domain/project/fgds-project";
import { OriginalAlarmDeviceConfiguration } from "@project/alarm-device-configuration/original-alarm-device-configuration";
import { FlameDetectorType } from "@project/flame-detector-configuration/flame-detector-type";
import { OriginalGasWarningCenterConfiguration } from "@project/gas-warning-center-configuration/original-gas-warning-center-configuration";
import { OriginalRegard7000PlusConfiguration } from "@project/gas-warning-center-configuration/regard-7000-plus/original-regard-7000-plus-configuration";
import { OriginalPlasticSignConfiguration } from "@project/plastic-sign-configuration/original-plastic-sign-configuration";
import { ProductItem } from "@project/product-item";
import { OriginalSignalElementConfiguration } from "@project/signal-element-configuration/original-signal-element-configuration";
import { OriginalTransmitterConfiguration } from "@project/transmitter-configuration/original-transmitter-configuration";
import { TransmitterType } from "@project/transmitter-configuration/transmitter-family-selector/transmitter-type";
import { first, forkJoin, map, Observable, of } from "rxjs";
import { OriginalFlameDetectorConfiguration } from "./flame-detector-configuration/original-flame-detector-configuration";

@Injectable()
export class OriginalProductConfigurationFactory {
  private productService = inject(ProductService);
  private gasService = inject(GasService);

  createOriginalGasWarningCenterConfiguration(project: Project, configId: string) {
    const productConfiguration = project.getGasWarningCenterById(configId);
    if (!productConfiguration) {
      return of();
    }
    const originalGasWarningCenter$ = this.productService.getProductById(productConfiguration.productId);
    const originalAttachments$ = productConfiguration.attachments.length
      ? this.mapAttachments(productConfiguration.attachments)
      : of([]);

    if (productConfiguration.regard3000Configuration) {
      return forkJoin([originalGasWarningCenter$, originalAttachments$]).pipe(
        first(),
        map(([gasWarningCenter, attachments]) => {
          const regard3000Configuration = productConfiguration.regard3000Configuration;
          if (!regard3000Configuration!.baseUnits || !regard3000Configuration!.lidUnits) {
            throw new Error("Missing required products for RegardPlus configuration");
          }
          const migrated = this.migrateLegacyRegard3000Attachments(regard3000Configuration!, attachments);
          const config = new Regard3000Configuration(
            migrated.baseUnits,
            regard3000Configuration!.lidUnits,
            migrated.inputModules,
            migrated.relayModules,
            migrated.gatewayModules,
            regard3000Configuration!.slotCovers,
          );

          return new OriginalGasWarningCenterConfiguration(productConfiguration, gasWarningCenter, migrated.attachments, config);
        }),
      );
    } else if (this.isLegacyRegard3000Product(productConfiguration.productId)) {
      return forkJoin([originalGasWarningCenter$, originalAttachments$]).pipe(
        first(),
        map(([gasWarningCenter, attachments]) => {
          if (!gasWarningCenter) {
            throw new Error("Missing required product for legacy Regard 3000 configuration");
          }

          const migrated = this.migrateLegacyRegard3000Attachments(undefined, attachments, gasWarningCenter);
          const config = new Regard3000Configuration(
            migrated.baseUnits,
            migrated.lidUnits,
            migrated.inputModules,
            migrated.relayModules,
            migrated.gatewayModules,
            migrated.slotCovers,
          );

          return new OriginalGasWarningCenterConfiguration(productConfiguration, gasWarningCenter, migrated.attachments, config);
        }),
      );
    } else if (productConfiguration.regard7000PlusConfiguration) {
      const config = productConfiguration.regard7000PlusConfiguration;
      const getProducts = (productItems: ProductItem[]): Observable<{ product: Product; quantity: number }[]> => {
        if (!productItems.length) {
          return of([]);
        }
        return forkJoin(
          productItems.map(({ productId, quantity }) =>
            this.productService.getProductById(productId).pipe(map((product) => (product ? { product, quantity } : undefined))),
          ),
        ).pipe(map((items) => items.filter((item): item is { product: Product; quantity: number } => !!item)));
      };

      const originalDockingStations$ = getProducts(config.dockingStations);
      const originalTerminalBlocks$ = getProducts(config.terminalBlocks);
      const originalInputModules$ = getProducts(config.inputModules);
      const originalRelayModules$ = getProducts(config.relayModules);
      const originalGatewayModules$ = getProducts(config.gatewayModules);
      const originalDashboards$ = getProducts(config.dashboards);

      return forkJoin([
        originalDockingStations$,
        originalTerminalBlocks$,
        originalInputModules$,
        originalRelayModules$,
        originalGatewayModules$,
        originalDashboards$,
        originalAttachments$,
      ]).pipe(
        first(),
        map(([dockingStations, terminalBlocks, inputModules, relayModules, gatewayModules, dashboards, attachments]) => {
          if (!dockingStations.length) {
            throw new Error("Missing required products for regard 7000 plus configuration");
          }
          const config = new OriginalRegard7000PlusConfiguration(
            dockingStations,
            inputModules,
            relayModules,
            gatewayModules,
            terminalBlocks,
            dashboards,
          );

          return new OriginalGasWarningCenterConfiguration(
            productConfiguration,
            dockingStations[0].product,
            attachments,
            undefined,
            config,
          );
        }),
      );
    } else {
      return forkJoin([originalGasWarningCenter$, originalAttachments$]).pipe(
        first(),
        map((products) => new OriginalGasWarningCenterConfiguration(productConfiguration, ...products)),
      );
    }
  }

  createOriginalTransmitterConfiguration(project: Project, configId: string) {
    const productConfiguration = project.getTransmitterById(configId);
    if (!productConfiguration) {
      return of();
    }
    const originalGas$ = productConfiguration.gasId ? this.gasService.getGasById(productConfiguration.gasId) : of(undefined);
    const originalTransmitter$ = this.productService.getProductById(productConfiguration.productId);
    const originalAttachments$ = productConfiguration.attachments.length
      ? this.mapAttachments(productConfiguration.attachments)
      : of([]);
    const originalSensor$ = productConfiguration.sensorId
      ? this.productService.getProductById(productConfiguration.sensorId)
      : of(undefined);

    return forkJoin([originalGas$, originalTransmitter$, originalAttachments$, originalSensor$]).pipe(
      first(),
      map(([gas, transmitter, attachments, sensor]) => {
        let originalFamily: TransmitterType | undefined;

        // Product metadata is authoritative when restoring an editable configuration.
        const parentFamilies = transmitter?.parentFamilies ?? [];
        const familyName = parentFamilies.length === 1 ? parentFamilies[0] : undefined;
        if (familyName) {
          originalFamily = TransmitterType.fromFamilyName(familyName);
        }

        const storedFamilyMatchesProduct = parentFamilies.some(
          (parentFamily) => TransmitterType.fromFamilyName(parentFamily).key === productConfiguration.family,
        );
        if (!originalFamily && productConfiguration.family && (!parentFamilies.length || storedFamilyMatchesProduct)) {
          originalFamily = TransmitterType.fromKey(productConfiguration.family);
        }

        // Legacy gas data cached before parentFamilies existed only carries rawName.
        if (!originalFamily && !parentFamilies.length && gas?.products && productConfiguration.productId) {
          const rawName = gas.products.find((gasProduct) => gasProduct.productId === productConfiguration.productId)?.rawName;
          if (rawName) {
            originalFamily = TransmitterType.fromFamilyName(rawName);
          }
        }

        return new OriginalTransmitterConfiguration(productConfiguration, gas, transmitter, attachments, sensor, originalFamily);
      }),
    );
  }

  createOriginalFlameDetectorConfiguration(project: Project, configId: string) {
    const productConfiguration = project.getFlameDetectorById(configId);
    if (!productConfiguration) {
      return of();
    }

    const originalSubstance$ = productConfiguration.substanceId
      ? this.gasService.getGasById(productConfiguration.substanceId)
      : of(undefined);
    const originalVariant$ = productConfiguration.variantId
      ? this.productService.getProductById(productConfiguration.variantId)
      : of(undefined);
    const originalAttachments$ = productConfiguration.attachments.length
      ? this.mapAttachments(productConfiguration.attachments)
      : of([]);

    return forkJoin([originalSubstance$, originalVariant$, originalAttachments$]).pipe(
      first(),
      map(([substance, variant, attachments]) => {
        let originalFamily: FlameDetectorType | undefined;

        // Product metadata is authoritative when restoring an editable configuration.
        if (variant) {
          const familyName = variant.parentFamilies?.[0];
          if (familyName) {
            originalFamily = FlameDetectorType.fromFamilyName(familyName);
          }
        }

        // Fallback for configurations without a family in the resolved product metadata.
        if (!originalFamily && productConfiguration.family) {
          originalFamily = FlameDetectorType.fromKey(productConfiguration.family);
        }

        if (!originalFamily && substance && productConfiguration.variantId) {
          // Legacy fallback: gas data cached before parentFamilies existed only carries the deprecated rawName.
          const rawName = substance.products.find((gp) => gp.productId === productConfiguration.variantId)?.rawName;
          if (rawName) {
            originalFamily = FlameDetectorType.fromFamilyName(rawName);
          }
        }

        return new OriginalFlameDetectorConfiguration(
          productConfiguration,
          substance,
          originalFamily,
          variant ?? undefined,
          attachments,
        );
      }),
    );
  }

  createOriginalAlarmDeviceConfiguration(project: Project, configId: string) {
    const productConfiguration = project.getAlarmDeviceById(configId);
    if (!productConfiguration) {
      return of();
    }
    const originalAlarmDevices$: Observable<Product[]> = this.productService.getProductsByIds(
      productConfiguration.productIds,
      FgdsProductType.ALARMDEVICE,
    );
    const originalAttachments$ = productConfiguration.attachments.length
      ? this.mapAttachments(productConfiguration.attachments)
      : of([]);
    return forkJoin([originalAlarmDevices$, originalAttachments$]).pipe(
      first(),
      map((products) => new OriginalAlarmDeviceConfiguration(productConfiguration, ...products)),
    );
  }

  createOriginalPlasticSignConfiguration(project: Project, configId: string) {
    const productConfiguration = project.getPlasticSignById(configId);
    if (!productConfiguration) {
      return of();
    }
    return this.productService.getProductById(productConfiguration.productId).pipe(
      first(),
      map((product) => new OriginalPlasticSignConfiguration(productConfiguration, product)),
    );
  }

  createOriginalSignalElementConfiguration(project: Project, configId: string) {
    const productConfiguration = project.getSignalElementById(configId);
    if (!productConfiguration) {
      return of();
    }
    return this.productService.getProductById(productConfiguration.productId).pipe(
      first(),
      map((product) => new OriginalSignalElementConfiguration(productConfiguration, product)),
    );
  }

  private mapAttachments(attachments: ProductItem[]) {
    const productIds = attachments.map((a) => a.productId);
    return this.productService.getProductsByIds(productIds, FgdsProductType.ATTACHMENT).pipe(
      map((products) =>
        products.map((product, i) => ({
          product,
          quantity: attachments[i].quantity,
        })),
      ),
    );
  }

  private migrateLegacyRegard3000Attachments(
    configuration: Regard3000Configuration | undefined,
    attachments: { product: Product; quantity: number }[],
    mainProduct?: Product,
  ) {
    const migrated = {
      attachments: [] as { product: Product; quantity: number }[],
      baseUnits: [...(configuration?.baseUnits ?? [])],
      lidUnits: [...(configuration?.lidUnits ?? [])],
      inputModules: [...(configuration?.inputModules ?? [])],
      relayModules: [...(configuration?.relayModules ?? [])],
      gatewayModules: [...(configuration?.gatewayModules ?? [])],
      slotCovers: configuration?.slotCovers,
    };

    if (mainProduct) {
      const existing = migrated.baseUnits.find((item) => item.productId === mainProduct.id);
      if (existing) {
        existing.quantity += 1;
      } else {
        migrated.baseUnits.push(ProductItem.create(mainProduct.id));
      }
    }

    for (const attachment of attachments) {
      const productType =
        FGDS_REGARD_3000_PLUS_MATERIAL_TYPE_MAP.get(attachment.product.id) ??
        this.getCanonicalRegard3000ProductType(attachment.product) ??
        resolveFgdsProductType({
          materialNumber: attachment.product.id,
          type: attachment.product.type,
          fullType: attachment.product.fullType,
        });
      const target = this.getRegard3000AttachmentTarget(productType);

      if (!target) {
        migrated.attachments.push(attachment);
        continue;
      }

      if (target === "slotCovers") {
        if (migrated.slotCovers?.productId === attachment.product.id) {
          migrated.slotCovers.quantity += attachment.quantity;
        } else {
          migrated.slotCovers = ProductItem.create(attachment.product.id, attachment.quantity);
        }
        continue;
      }

      const existing = migrated[target].find((item) => item.productId === attachment.product.id);
      if (existing) {
        existing.quantity += attachment.quantity;
      } else {
        migrated[target].push(ProductItem.create(attachment.product.id, attachment.quantity));
      }
    }

    return migrated;
  }

  private getCanonicalRegard3000ProductType(product: Product): string | undefined {
    const productName = product.name?.toLocaleLowerCase() ?? "";
    if (productName.includes("regard 3000 display unit") || productName.includes("regard 3000 deckeleinheit")) {
      return "z-n-gwz-db";
    }

    switch (product.fullType) {
      case "gwz-base":
      case "z-o-gwz-ds":
      case "z-o-gwz-input-analog":
      case "z-o-gwz-relais":
      case "z-o-gwz-gateway":
      case "z-n-gwz-db":
      case "z-o-slotcover":
        return product.fullType;
      default:
        return undefined;
    }
  }

  private getRegard3000AttachmentTarget(
    productType: string | undefined,
  ): "baseUnits" | "lidUnits" | "inputModules" | "relayModules" | "gatewayModules" | "slotCovers" | undefined {
    switch (productType) {
      case "gwz-base":
      case "z-o-gwz-ds":
        return "baseUnits";
      case "z-n-gwz-db":
        return "lidUnits";
      case "z-o-gwz-input-analog":
        return "inputModules";
      case "z-o-gwz-relais":
        return "relayModules";
      case "z-o-gwz-gateway":
        return "gatewayModules";
      case "z-o-slotcover":
        return "slotCovers";
      default:
        return undefined;
    }
  }

  private isLegacyRegard3000Product(productId: string): boolean {
    return productId === "3705684";
  }
}
